import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navigation from "../components/Navigation";
import Seo, { breadcrumbs } from "../components/Seo";
import { SITE_NAME, absUrl, toDescription, toISODate } from "../lib/seo";
import { Skeleton } from "../components/Skeleton";
import { useMinLoading } from "../lib/useMinLoading";
import "../components/styles/BlogPost.css";

const BlogPost = () => {
  const { slug } = useParams(); // This is actually the document ID now
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const containerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
  };

  // Fetch blog post from Firebase by document ID
  useEffect(() => {
    const fetchPost = async () => {
      try {
        const { doc, getDoc } = await import("firebase/firestore");
        const { db } = await import("../firebase");

        // Get document by ID
        const postDoc = doc(db, "blog", slug);
        const postSnapshot = await getDoc(postDoc);

        if (postSnapshot.exists() && postSnapshot.data().status === "live") {
          const data = postSnapshot.data();
          const postData = {
            id: postSnapshot.id,
            ...data,
            // The display fields below are formatted for humans and lose the
            // machine-readable form. Keep the raw values so <Seo> can emit
            // real ISO dates in article:published_time / datePublished.
            publishedISO: toISODate(data.date),
            modifiedISO: toISODate(data.updatedAt || data.date),
            // Convert Firebase Timestamp to readable string if it exists
            date:
              data.date && data.date.toDate
                ? data.date.toDate().toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : data.date,
            // Handle any other potential Timestamp fields
            createdAt:
              data.createdAt && data.createdAt.toDate
                ? data.createdAt.toDate().toLocaleDateString()
                : data.createdAt,
            updatedAt:
              data.updatedAt && data.updatedAt.toDate
                ? data.updatedAt.toDate().toLocaleDateString()
                : data.updatedAt,
          };
          setPost(postData);
        } else {
          console.error("❌ No post found with ID:", slug);
          setError("Post not found");
        }
      } catch (err) {
        console.error("❌ Error fetching post:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  // Enhanced markdown-like content processor
  const processContent = (content) => {
    return content
      .replace(/\n\n/g, "</p><p>")
      .replace(/\n/g, "<br />")
      .replace(/## (.*?)(<br \/>|<\/p>)/g, "</p><h2>$1</h2><p>")
      .replace(/### (.*?)(<br \/>|<\/p>)/g, "</p><h3>$1</h3><p>")
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/^/, "<p>")
      .replace(/$/, "</p>")
      .replace(/<p><\/p>/g, "")
      .replace(/<p><h/g, "<h")
      .replace(/h><\/p>/g, "h>");
  };

  const showSkeleton = useMinLoading(loading);

  if (showSkeleton) {
    return (
      <div className="blog-post-page">
        <Navigation />
        <div className="blog-post-container" aria-busy="true" aria-label="Loading post">
          <div className="post-header">
            <Link to="/blog" className="back-link">
              ← Back to Blog
            </Link>
            <Skeleton width="45%" height={14} style={{ marginBottom: 20 }} />
            <Skeleton width="95%" height={56} style={{ marginBottom: 12 }} />
            <Skeleton width="70%" height={56} />
          </div>
          <div className="post-content">
            <Skeleton width="100%" height={14} style={{ marginBottom: 14 }} />
            <Skeleton width="95%" height={14} style={{ marginBottom: 14 }} />
            <Skeleton width="100%" height={14} style={{ marginBottom: 14 }} />
            <Skeleton width="80%" height={14} style={{ marginBottom: 14 }} />
            <Skeleton width="60%" height={14} style={{ marginBottom: 32 }} />
            <Skeleton width="100%" height={14} style={{ marginBottom: 14 }} />
            <Skeleton width="95%" height={14} style={{ marginBottom: 14 }} />
            <Skeleton width="80%" height={14} style={{ marginBottom: 14 }} />
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-post-page">
        {/* Draft, deleted, or bad slug. Served as a 200 by the SPA fallback,
            so noindex is the only thing keeping it out of the index. */}
        <Seo
          title="Post not found — bboy creative"
          description="This post isn't available. Browse the rest of the blog instead."
          path={`/blog/${slug}`}
          noindex
        />
        <Navigation />
        <div className="blog-post-container">
          <div className="post-header">
            <Link to="/blog" className="back-link">
              ← Back to Blog
            </Link>
            <h1>Post Not Found</h1>
            <p>The blog post you're looking for doesn't exist.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-post-page">
      <Seo
        title={`${post.title} — bboy creative`}
        description={post.excerpt || post.content}
        path={`/blog/${post.id}`}
        type="article"
        publishedTime={post.publishedISO}
        modifiedTime={post.modifiedISO}
        keywords={[post.category, "San Antonio", "app development", "bboy creative"]
          .filter(Boolean)
          .join(", ")}
        jsonLd={[
          {
            "@type": "BlogPosting",
            "@id": absUrl(`/blog/${post.id}#post`),
            headline: post.title,
            description: toDescription(post.excerpt || post.content),
            url: absUrl(`/blog/${post.id}`),
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": absUrl(`/blog/${post.id}`),
            },
            ...(post.publishedISO ? { datePublished: post.publishedISO } : {}),
            ...(post.modifiedISO ? { dateModified: post.modifiedISO } : {}),
            ...(post.category ? { articleSection: post.category } : {}),
            author: { "@type": "Organization", name: SITE_NAME },
            publisher: {
              "@type": "Organization",
              name: SITE_NAME,
              logo: {
                "@type": "ImageObject",
                url: absUrl("/logo512.png"),
              },
            },
          },
          breadcrumbs([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
            { name: post.title, path: `/blog/${post.id}` },
          ]),
        ]}
      />

      <Navigation />

      <motion.article
        className="blog-post-container"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="post-header">
          <Link to="/blog" className="back-link">
            ← Back to Blog
          </Link>
          <div className="post-meta">
            <span className="category">{post.category}</span>
            <span className="date">{post.date}</span>
            <span className="read-time">{post.readTime}</span>
          </div>
          <h1>{post.title}</h1>
        </div>
        <div
          className="post-content"
          dangerouslySetInnerHTML={{
            __html: processContent(
              post.content || post.excerpt || "Content not available."
            ),
          }}
        />
        ``
        <div className="post-footer">
          <Link to="/blog" className="back-to-blog">
            ← Back to Blog
          </Link>
          <div className="contact-cta">
            <h3>Ready to start your project?</h3>
            <a href="mailto:jm@bboycreative.com" className="btn-primary">
              Get in Touch
            </a>
          </div>
        </div>
      </motion.article>
    </div>
  );
};

export default BlogPost;
